import {
  ListGroup, Row, Col, Button, Form
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useStateMachine } from "little-state-machine";
import { ApplicationTypeDisplay } from "interfaces/models/ApplicationTypeDisplay";
import { updateSelectedUserApplicationDetails } from "actions/updateSelectedUserApplicationDetails";
import IUserApplicationDetails from "interfaces/models/IUserApplicationDetails";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { XCircleFill } from "react-bootstrap-icons";
import "./ApplicationsAllocationItem.scss";
import { updateAssignWhitespacePermissionsRequest } from "actions/updateAssignWhitespacePermissionsRequest";
import { ApplicationType } from "interfaces/models/ApplicationType";
import { updateAssignHwrcPermissionsRequest } from "actions/updateAssignHwrcPermissionsRequest";

interface ApplicationsAllocationItemProps {
  userApplicationDetails: IUserApplicationDetails;
  handleIsSending: (isSending : boolean)=> void;
  handleError: (errors : string[])=> void;
}

const ApplicationsAllocationItem = ({
  userApplicationDetails, handleIsSending, handleError, ...props
} : ApplicationsAllocationItemProps) => {
  const { state } = useStateMachine();
  const { actions } = useStateMachine({
    updateSelectedUserApplicationDetails,
    updateAssignWhitespacePermissionsRequest,
    updateAssignHwrcPermissionsRequest
  });

  const {
    isSending, errors, statusCode, sendRequest, resetState
  } = useAmplifyREST();

  const navigate = useNavigate();
  const [isAllocated, setIsAllocated] = useState(userApplicationDetails.isAllocated);

  const handleConfigureAllocation = () => {
    let url : string = "";

    switch (userApplicationDetails.applicationDetails.applicationType) {
      case ApplicationType.Whitespace:
      {
        url = "/assignuserwhitespaceroles";
        actions.updateSelectedUserApplicationDetails(userApplicationDetails);
        actions.updateAssignWhitespacePermissionsRequest(undefined);

        break;
      }
      case ApplicationType.HWRC:
      {
        url = "/assignuserhwrcroles";
        actions.updateSelectedUserApplicationDetails(userApplicationDetails);
        actions.updateAssignHwrcPermissionsRequest(undefined);
        break;
      }
      default:
      {
        const applicationTypeErrors : string[] = [`Unknown application type ${userApplicationDetails.applicationDetails.applicationType}`];
        handleError(applicationTypeErrors);
        break;
      }
    }

    if (url !== "") {
      navigate(url);
    }
  };

  const toggleConfigureButtonEnablement = (event: any) => {
    event.preventDefault();
    resetState();
    const method = "DELETE";
    // eslint-disable-next-line max-len
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/apps/${encodeURIComponent(userApplicationDetails.applicationDetails.applicationId)}`;
    sendRequest(url, method);
    handleIsSending(true);
    return false;
  };

  useEffect(() => {
    if (statusCode !== 0) {
      handleIsSending(false);
    }
    if (statusCode === 204) {
      setIsAllocated(!isAllocated);
    }
  }, [statusCode]);

  useEffect(() => {
    if (errors) {
      handleError(errors!);
    }
  }, [errors]);

  return (
    <ListGroup.Item {...props}>
      <Row className="align-items-center" data-testid="applicationsAllocationItemRow">
        <Col data-testid="siteUrl" xs={4} md={3}>{userApplicationDetails.applicationDetails.siteUrl}</Col>
        <Col data-testid="applicationType" xs={4} md={2}>{ApplicationTypeDisplay[userApplicationDetails.applicationDetails.applicationType]}</Col>
        <Col data-testid="groupName" xs={4} md={3}>{userApplicationDetails.group?.groupName}</Col>
        <Col xs={6} md={2} className="d-flex align-items-center">
          <Form.Check
            data-testid="swAllocate"
            className="d-inline-flex custom-switch custom-switch-md ms-2 allocation-wrapper"
            type="switch"
            aria-required="false"
            aria-label="Allocate application"
            disabled={!isAllocated}
            checked={isAllocated}
            value={userApplicationDetails.applicationDetails.applicationId}
            onClick={toggleConfigureButtonEnablement}
            onChange={() => {}}
          />
          {isSending && <span role="status" className="d-none">sending....</span>}
          {errors && <span data-testid="error"><XCircleFill color="red" className="ms-2" /></span>}
        </Col>

        <Col xs={6} md={2} className="d-flex justify-content-end">
          <Button
            data-testid="btnConfigure"
            variant="primary"
            className="right-margin-small"
            aria-label="Configure"
            onClick={() => handleConfigureAllocation()}
          >
            Configure
          </Button>
        </Col>
      </Row>
    </ListGroup.Item>
  );
};

export default ApplicationsAllocationItem;
